/**
 * Whether the position on the chart can be trusted right now.
 *
 * One place, because three things ask: the boat symbol (which turns red when the
 * answer is no), the readouts, and anything else that would rather not present a
 * guess as a fix.
 *
 * THE TEST IS AGE, AND ONLY AGE. A receiver gives us a fix about once a second,
 * and when they stop arriving the last one sits on the chart looking perfectly
 * healthy while the boat carries on without it. Past a short grace that is stale,
 * past a longer one lost, and both mean the same thing to a skipper: what you are
 * looking at is where you were, not where you are.
 *
 * It was briefly ALSO gated on the device source reporting 'live', to catch the
 * cached position we take at startup. That was wrong, and wrong in a way worth
 * writing down. When the background location service owns the seam, which it does
 * whenever the anchor watch is down or a background alarm is armed, it reports
 * its status into the background-service store and the foreground watch stands
 * down, so `deviceStatus` sits at 'idle' with a perfectly good fix arriving every
 * second. The boat went red and stayed red. A rule about whether a position is
 * trustworthy must not depend on which of two feeds happens to be delivering it.
 *
 * The startup cached position is handled where it belongs instead: its wall time
 * is recorded as the time it was actually taken, not the moment we received it,
 * so a position from the car park an hour ago is an hour old and reads as lost,
 * and one from thirty seconds ago is thirty seconds old and reads as fine. Which
 * is exactly what it is.
 *
 * A device with no fix at all reads as lost too (GnssHealthRunner bands a null
 * last-fix time straight to lost), so "we have not placed the boat yet" needs no
 * special case here.
 *
 * The simulator is never unreliable. It knows exactly where the boat is, because
 * it is the one moving it.
 */
import type { FixHealth } from './fixHealth';

export interface FixTrustInputs {
  /** Which source is feeding us. */
  sourceKind: 'sim' | 'device';
  /** Age band of the last fix, or null when the simulator is driving. */
  fixHealth: FixHealth | null;
}

/** True when the position on screen should not be presented as a live fix. */
export function fixUnreliable({ sourceKind, fixHealth }: FixTrustInputs): boolean {
  if (sourceKind !== 'device') return false;
  return fixHealth === 'stale' || fixHealth === 'lost';
}
